import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL, CountryOptions } from '../lib/utils';
import { usePageTitle } from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, MoreHorizontal, Pencil, Trash2, Building, Search, Loader2, Paperclip, X, FileText, Image, Eye, Upload } from 'lucide-react';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TrainingCenters = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  usePageTitle('Training Centers');
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCenter, setEditingCenter] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    email: '',
    primary_contact: '',
    secondary_contact: '',
    address: '',
    city: '',
    country: '',
    registration_number: '',
    vat_number: '',
    default_signature_url: ''
  });
  const [uploadingSignature, setUploadingSignature] = useState(false);

  const fetchCenters = async () => {
    try {
      const response = await axios.get(`${API_URL}/training-centers`);
      setCenters(response.data);
    } catch (error) {
      toast.error('Failed to fetch training centers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCenters();
  }, []);

  // Handle edit query parameter from TrainingCenterDetail page
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && centers.length > 0 && !loading) {
      const centerToEdit = centers.find(c => c.id === editId);
      if (centerToEdit) {
        handleEdit(centerToEdit);
        // Clear the query param after opening dialog
        setSearchParams({});
      }
    }
  }, [searchParams, centers, loading]);

  const resetForm = () => {
    setFormData({
      name: '',
      domain: '',
      email: '',
      primary_contact: '',
      secondary_contact: '',
      address: '',
      city: '',
      country: '',
      registration_number: '',
      vat_number: '',
      default_signature_url: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Please enter a name');
      return;
    }

    setSaving(true);
    
    try {
      const payload = { ...formData };
      Object.keys(payload).forEach(key => {
        if (payload[key] === '') payload[key] = null;
      });
      payload.name = formData.name;
      
      if (editingCenter) {
        await axios.put(`${API_URL}/training-centers/${editingCenter.id}`, payload);
        toast.success('Training center updated successfully');
      } else {
        await axios.post(`${API_URL}/training-centers`, payload);
        toast.success('Training center created successfully');
      }
      
      setDialogOpen(false);
      setEditingCenter(null);
      resetForm();
      fetchCenters();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to save training center';
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || String(e)).join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (center) => {
    setEditingCenter(center);
    setFormData({
      name: center.name,
      domain: center.domain || '',
      email: center.email || '',
      primary_contact: center.primary_contact || '',
      secondary_contact: center.secondary_contact || '',
      address: center.address || '',
      city: center.city || '',
      country: center.country || '',
      registration_number: center.registration_number || '',
      vat_number: center.vat_number || '',
      default_signature_url: center.default_signature_url || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (center) => {
    setDeleteConfirm(center);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    
    const center = deleteConfirm;
    setDeleteConfirm(null);
    
    try {
      await axios.delete(`${API_URL}/training-centers/${center.id}`);
      toast.success('Training center deleted successfully');
      fetchCenters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete training center');
    }
  };

  const handleFileUpload = async (centerId, file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API_URL}/training-centers/${centerId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('File uploaded successfully');
      fetchCenters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (centerId, attachmentId) => {
    try {
      await axios.delete(`${API_URL}/training-centers/${centerId}/attachments/${attachmentId}`);
      toast.success('Attachment deleted');
      fetchCenters();
    } catch (error) {
      toast.error('Failed to delete attachment');
    }
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return <Image className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  const filteredCenters = centers.filter(center =>
    center.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (center.city && center.city.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="h-96 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="training-centers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Training Centers
          </h1>
          <p className="text-slate-500 mt-1">
            Manage training center locations and details
          </p>
        </div>
        <Button 
          className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
          onClick={() => {
            setEditingCenter(null);
            resetForm();
            setDialogOpen(true);
          }}
          data-testid="add-center-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Training Center
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search training centers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="center-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Training Centers Table */}
      <Card>
        <CardContent className="p-0">
          {filteredCenters.length === 0 ? (
            <div className="text-center py-12">
              <Building className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No training centers found</h3>
              <p className="text-slate-500 mt-1">
                {searchTerm ? 'Try a different search term' : 'Get started by adding your first training center'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Name</TableHead>
                  <TableHead className="font-semibold">Location</TableHead>
                  <TableHead className="font-semibold">Email</TableHead>
                  <TableHead className="font-semibold">Primary Contact</TableHead>
                  <TableHead className="font-semibold">Attachments</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCenters.map((center) => (
                  <TableRow 
                    key={center.id} 
                    className="hover:bg-slate-50/50 cursor-pointer" 
                    data-testid={`center-row-${center.id}`}
                    onClick={() => navigate(`/training-centers/${center.id}`)}
                  >
                    <TableCell className="font-medium text-[#2C9DE1] hover:underline">
                      {center.name}
                    </TableCell>
                    <TableCell>
                      {center.city && center.country 
                        ? `${center.city}, ${center.country}` 
                        : center.city || center.country || '-'}
                    </TableCell>
                    <TableCell>{center.email || '-'}</TableCell>
                    <TableCell>{center.primary_contact || '-'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Paperclip className="w-4 h-4 text-slate-400" />
                        <span>{center.attachments?.length || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`center-actions-${center.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/training-centers/${center.id}`)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEdit(center)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = 'image/*,.pdf,.doc,.docx';
                              input.onchange = (e) => {
                                const file = e.target.files[0];
                                if (file) handleFileUpload(center.id, file);
                              };
                              input.click();
                            }}
                          >
                            <Paperclip className="w-4 h-4 mr-2" />
                            Upload File
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(center)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editingCenter ? 'Edit Training Center' : 'Add New Training Center'}
            </DialogTitle>
            <DialogDescription>
              {editingCenter ? 'Update training center details' : 'Create a new training center'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="form-section-header">Basic Information</h3>
              
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Training Center Name"
                  data-testid="center-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="domain">Domain</Label>
                  <Input
                    id="domain"
                    value={formData.domain}
                    onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                    placeholder="example.com"
                    data-testid="center-domain-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="contact@example.com"
                    data-testid="center-email-input"
                  />
                </div>
              </div>
            </div>

            {/* Contact Persons */}
            <div className="space-y-4">
              <h3 className="form-section-header">Contact Persons</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="primary_contact">Primary Contact</Label>
                  <Input
                    id="primary_contact"
                    value={formData.primary_contact}
                    onChange={(e) => setFormData({ ...formData, primary_contact: e.target.value })}
                    placeholder="John Doe"
                    data-testid="center-primary-contact-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="secondary_contact">Secondary Contact</Label>
                  <Input
                    id="secondary_contact"
                    value={formData.secondary_contact}
                    onChange={(e) => setFormData({ ...formData, secondary_contact: e.target.value })}
                    placeholder="Jane Doe"
                    data-testid="center-secondary-contact-input"
                  />
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="space-y-4">
              <h3 className="form-section-header">Location</h3>
              
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="123 Main Street"
                  data-testid="center-address-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Nicosia"
                    data-testid="center-city-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Select
                    value={formData.country || "none"}
                    onValueChange={(value) => setFormData({ ...formData, country: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="center-country-select">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select country</SelectItem>
                      {CountryOptions.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Registration */}
            <div className="space-y-4">
              <h3 className="form-section-header">Registration Details</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="registration_number">Company Registration Number</Label>
                  <Input
                    id="registration_number"
                    value={formData.registration_number}
                    onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                    placeholder="REG12345"
                    data-testid="center-reg-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vat_number">VAT Number</Label>
                  <Input
                    id="vat_number"
                    value={formData.vat_number}
                    onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                    placeholder="CY12345678X"
                    data-testid="center-vat-input"
                  />
                </div>
              </div>
            </div>

            {/* Default Signature */}
            <div className="space-y-4">
              <h3 className="form-section-header">Default Signature</h3>
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Input
                      type="file"
                      accept="image/*"
                      id="center_signature_upload"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 2 * 1024 * 1024) {
                          toast.error('File size must be less than 2MB');
                          return;
                        }
                        setUploadingSignature(true);
                        const formDataUpload = new FormData();
                        formDataUpload.append('file', file);
                        try {
                          const response = await axios.post(`${API_URL}/uploads/signature`, formDataUpload, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });
                          setFormData(prev => ({ ...prev, default_signature_url: response.data.path }));
                          toast.success('Signature uploaded successfully');
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Failed to upload signature');
                        } finally {
                          setUploadingSignature(false);
                          e.target.value = '';
                        }
                      }}
                      data-testid="center-signature-upload"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('center_signature_upload').click()}
                      disabled={uploadingSignature}
                      className="flex items-center gap-2"
                      data-testid="center-signature-upload-btn"
                    >
                      {uploadingSignature ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {formData.default_signature_url ? 'Change' : 'Upload'}
                    </Button>
                    {formData.default_signature_url && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setFormData({ ...formData, default_signature_url: '' })}
                        data-testid="center-signature-remove-btn"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Remove
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Auto-applied as the signature on certificates for new classes at this training center. Can be overridden per class.
                  </p>
                </div>
                {formData.default_signature_url && (
                  <div className="border rounded-lg p-2 bg-slate-50">
                    <img
                      src={`${BACKEND_URL}${formData.default_signature_url}`}
                      alt="Default signature"
                      className="max-h-12 max-w-[100px] object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Attachments (only when editing) */}
            {editingCenter && (
              <div className="space-y-4">
                <h3 className="form-section-header">Attachments</h3>
                
                {/* Existing attachments */}
                {editingCenter.attachments?.length > 0 && (
                  <div className="space-y-2">
                    {editingCenter.attachments.map((att) => (
                      <div key={att.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          {getFileIcon(att.content_type)}
                          <a 
                            href={`${API_URL}/files/${att.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-[#2C9DE1] hover:underline"
                          >
                            {att.filename}
                          </a>
                          <Badge variant="secondary" className="text-xs">
                            {(att.size / 1024).toFixed(1)} KB
                          </Badge>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteAttachment(editingCenter.id, att.id)}
                        >
                          <X className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Upload new file */}
                <div className="flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        handleFileUpload(editingCenter.id, e.target.files[0]);
                        e.target.value = '';
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Paperclip className="w-4 h-4 mr-2" />
                        Add Attachment
                      </>
                    )}
                  </Button>
                  <span className="text-xs text-slate-500">
                    PDF, Word, or images (max 10MB)
                  </span>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
                disabled={saving}
                data-testid="center-submit-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingCenter ? 'Update Center' : 'Create Center'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <Dialog open={true} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                Delete Training Center
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2 mt-4">
              <Button
                variant="outline"
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white border-0"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={confirmDelete}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default TrainingCenters;
